import { h, Component } from 'preact';
import util from 'preact-util';
import { route } from 'preact-router';
import { observer } from 'mobx-preact';
import { IntlProvider, Text, Localizer, withText } from 'preact-i18n';
import Markdown from 'preact-markdown';

import localUtil from '../../lib/util';
import fields from '../../lib/fields';
import { v4 as uuidv4 } from 'uuid';
import { buildOptimisticMessage, buildEditPatch } from '../../lib/optimisticMessage';
import { buildMessageRenderList } from '../../lib/inboxMessageGroups';
import { getDraft, setDraft, clearDraft } from '../../lib/inboxDrafts';
import { findQuotedMessage, quoteSnippet } from '../../lib/inboxReply';
import { signalUser, signalCustomer, subscribeUserSignal, subscribeCustomerSignal, sendTyping, subscribeTyping, conversationId, isTypingActive, shouldSendTyping } from '../../lib/inboxRealtime';

import FormFields from '../../components/form/fields';
import Input from '../../components/form/input';
import Camera from '../../components/gui/camera';

import Placeholder from '../../components/gui/placeholder';

import EmojiPicker from '../gui/emojiPicker';
import ImageScrollerFullscreen from '../gui/imageScrollerFullscreen';
import PresenceDot from '../gui/presenceDot';
import InboxMessage from './message';
import { appConfirm } from '../../lib/dialogs';
import { beginDialogAction } from '../../lib/dialogAction';

@withText(props => ({
    titleText: <Text id='inbox.search-title'>Search for new users...</Text>,
    sendMessageText: <Text id='inbox.send-message'>Type a message and hit enter to send...</Text>,
    searchMessagesText: <Text id='inbox.searchMessages'>Search messages…</Text>,
}))
@observer
class Inbox extends Component {
    constructor(props) {
        super(props);
        this.state = {
            selectedIndex: 0,
            disabled: false,
            searching: false,
            focus: false,
            expandedReadBy: {},
            loadingMore: false,
            showFullscreen: false,
            fullscreenImages: [],
            attachedImages: [],
            uploadingImages: false,
            showCamera: false,
            showEmojiPicker: false,
            replyToUuidv4: null,
            typingName: null,
            messageSearch: '',
            openMessageUuid: null,
        };
        this.searchInputField = null;
        this.messageInputField = null;
        this.messageContainer = null;
        this.searchTimer = null;
        this.markAsReadTimer = null;
        this.limitMessages = 25;
        // How many older messages each scroll-up "page" pulls in.
        this.pageSize = 25;
        // Grows as the user loads older messages. EVERY refetch (poll, send,
        // mark-read, load-more) uses this so the window never collapses back.
        this.loadedLimit = 25;
        // Synchronous re-entry guard for scroll-triggered loading (separate from
        // the loadingMore state flag, which only drives the spinner).
        this.isLoadingMore = false;
        // Trigger auto-load when scrolled within this many px of the top.
        this.scrollLoadThreshold = 80;
        this.signalUnsub = null;
        this.customerSignalUnsub = null;
        this.typingUnsub = null;
        this.typingClearTimer = null;
        this.lastTypingSentMs = 0;
    }

    loadAll = async (props = this.props) => {
        const { messageStore, userStore } = this.props.stores;
        const { user } = userStore;

        // loadAll re-runs on mount, prop change, and after a delete — tear down
        // any prior pollers/realtime listeners first so they don't accumulate
        // (orphaned onSnapshot listeners keep billing and fire stale refetches).
        if (this.conversationListInterval) { clearInterval(this.conversationListInterval); this.conversationListInterval = null; }
        if (this.conversationInterval) { clearInterval(this.conversationInterval); this.conversationInterval = null; }
        if (this.signalUnsub) { this.signalUnsub(); this.signalUnsub = null; }
        if (this.customerSignalUnsub) { this.customerSignalUnsub(); this.customerSignalUnsub = null; }

        messageStore.cleanupConversation();
        messageStore.updateKeyValue('search', '');

        await messageStore.getConversationList();

        this.setFocus();
        this.scrollTimer = setTimeout(() => {
            this.scrollMessagesToBottom();
        }, 1000);

        this.conversationListInterval = setInterval(() => {
            this.updateConversationList();
        }, 60000);

        this.conversationInterval = setInterval(() => {
            this.updateConversation();
        }, 60000);

        this.signalUnsub = subscribeUserSignal(user && user.uuidv4, () => {
            this.updateConversationList();
            if (this.state.toUserUuidv4) { this.updateConversation(); }
        });
        const customerId = userStore.customer && userStore.customer.id;
        this.customerSignalUnsub = subscribeCustomerSignal(customerId, () => {
            this.updateConversationList();
            if (this.state.toUserUuidv4 === '@all') { this.updateConversation(); }
        });

        // Deep-link: opened with a target user (profile drawer "Send message"
        // button, /users/profile/inbox/:uuidv4). Enter that conversation
        // directly — same path as picking a search result, which also handles
        // users never messaged before.
        const targetUuidv4 = props.uuidv4;
        if (targetUuidv4 && targetUuidv4 !== this.state.toUserUuidv4) {
            this.setState({ toUserUuidv4: targetUuidv4 });
            await this.loadConversation(targetUuidv4, this.limitMessages, 0);
            requestAnimationFrame(() => this.scrollMessagesToBottom());
        }
    }

    scrollMessagesToBottom = () => {
        const el = this.messageContainer;
        if (!el) return;
        el.scrollTo({ top: el.scrollHeight, behavior: 'auto' });
    }

    scrollToFirstUnreadOrBottom = () => {
        if (this.firstUnreadEl && this.messageContainer) {
            this.firstUnreadEl.scrollIntoView({ block: 'center', behavior: 'auto' });
            return;
        }
        this.scrollMessagesToBottom();
    }

    // Snapshot the scroll position before a refetch so we can keep the viewport
    // steady afterwards. nearBottom lets pollers decide whether to follow new
    // messages down (you were at the bottom) or leave you where you were reading.
    captureScroll = () => {
        const el = this.messageContainer;
        if (!el) {
            return null;
        }
        const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        return {
            prevHeight: el.scrollHeight,
            prevTop: el.scrollTop,
            nearBottom: distanceFromBottom < this.scrollLoadThreshold,
        };
    }

    restoreScroll = (snapshot, { stickToBottom = false } = {}) => {
        const el = this.messageContainer;
        if (!el || !snapshot) {
            return;
        }
        requestAnimationFrame(() => {
            if (stickToBottom && snapshot.nearBottom) {
                el.scrollTo({ top: el.scrollHeight, behavior: 'auto' });
            } else {
                // Older messages prepend at the top and grow scrollHeight; offset
                // scrollTop by the delta so the same messages stay under the viewport.
                el.scrollTop = snapshot.prevTop + (el.scrollHeight - snapshot.prevHeight);
            }
        });
    }

    updateConversation = async () => {
        const { toUserUuidv4 } = this.state;
        // console.log('updateConversation', { toUserUuidv4 });
        if (!toUserUuidv4) {
            return;
        }
        const { messageStore } = this.props.stores;
        // Don't refetch mid-send: it would replace the array and flicker the
        // optimistic bubble away before the server has persisted it.
        if (messageStore.messages.some(m => m.pending)) {
            return;
        }
        const snapshot = this.captureScroll();
        // Refetch the full loaded window (not the default) so loaded-more history
        // survives the poll. This is the core fix for "drops back to default".
        await messageStore.getConversation(toUserUuidv4, this.loadedLimit, 0);
        this.restoreScroll(snapshot, { stickToBottom: true });
        this.markAsReadTimer = setTimeout(() => {
            this.markAllAsRead();
        }, 1000);
    }

    updateConversationList = async () => {
        const { messageStore } = this.props.stores;
        await messageStore.getConversationList();
        await messageStore.getUnreadCount();
    }

    subscribeTypingForConversation = (toUserUuidv4) => {
        if (this.typingUnsub) { this.typingUnsub(); this.typingUnsub = null; }
        this.setState({ typingName: null });
        const { user } = this.props.stores.userStore;
        if (!toUserUuidv4 || toUserUuidv4 === '@all' || toUserUuidv4 === (user && user.uuidv4)) {
            return; // typing only for 1:1 DMs
        }
        const convId = conversationId(user.uuidv4, toUserUuidv4);
        this.typingUnsub = subscribeTyping(convId, (data) => {
            const name = isTypingActive(data, user.uuidv4, Date.now());
            this.setState({ typingName: name });
            if (this.typingClearTimer) { clearTimeout(this.typingClearTimer); }
            if (name) {
                this.typingClearTimer = setTimeout(() => this.setState({ typingName: null }), 5000);
            }
        });
    };

    loadConversation = async (toUserUuidv4, limit, offset) => {
        const { messageStore } = this.props.stores;
        // Opening a conversation starts a fresh window at the default size.
        this.loadedLimit = limit || this.limitMessages;
        this.isLoadingMore = false;
        this.setState({ loadingMore: false });
        messageStore.cleanupConversation();
        messageStore.updateKeyValue('search', '');
        // Restore any saved draft for the conversation being opened.
        messageStore.updateKeyValue('messageContent', getDraft(toUserUuidv4));
        await messageStore.getConversation(toUserUuidv4, this.loadedLimit, offset);
        this.markAsReadTimer = setTimeout(() => {
            this.markAllAsRead();
        }, 1000);
        await messageStore.getUnreadCount();
        this.setFocusMessage();
        // Reset before the grouped list re-renders; the new-messages divider
        // sets it again via ref if there are unread received messages.
        this.firstUnreadEl = null;
        requestAnimationFrame(() => this.scrollToFirstUnreadOrBottom());
        this.subscribeTypingForConversation(toUserUuidv4);
    }

    // Triggered by scrolling near the top. Grows the window by one page of older
    // messages and keeps the viewport pinned to what the user was reading.
    loadMoreMessages = async () => {
        const { toUserUuidv4 } = this.state;
        const { messageStore } = this.props.stores;
        const { messages, messagesTotal } = messageStore;
        if (!toUserUuidv4 || this.isLoadingMore) {
            return;
        }
        if (messages.length >= messagesTotal) {
            return; // Reached the start of the conversation — nothing older to load.
        }
        this.isLoadingMore = true;
        this.setState({ loadingMore: true });
        const snapshot = this.captureScroll();
        this.loadedLimit += this.pageSize;
        await messageStore.getConversation(toUserUuidv4, this.loadedLimit, 0);
        this.restoreScroll(snapshot, { stickToBottom: false });
        this.isLoadingMore = false;
        this.setState({ loadingMore: false });
    }

    handleMessagesScroll = (e) => {
        const el = e.target;
        if (!el) {
            return;
        }
        if (el.scrollTop <= this.scrollLoadThreshold) {
            this.loadMoreMessages();
        }
    }

    loadConversationList = async () => {
        const { messageStore } = this.props.stores;
        messageStore.cleanupConversation();
        messageStore.updateKeyValue('search', '');
        await messageStore.getConversationList();
        await messageStore.getUnreadCount();
    }

    markAllAsRead = async () => {
        const { toUserUuidv4 } = this.state;
        const { messageStore } = this.props.stores;
        const { messages } = messageStore;
        if (messages && messages.length > 0) {
            const unreadMessages = messages.filter(m => !m.readDate);
            if (unreadMessages.length > 0) {
                const snapshot = this.captureScroll();
                // Pass loadedLimit so the read-refresh keeps the loaded window.
                await messageStore.markMessageRead(toUserUuidv4, unreadMessages.map(m => m.uuidv4).join(','), this.loadedLimit);
                this.restoreScroll(snapshot, { stickToBottom: true });
                if (toUserUuidv4 && toUserUuidv4 !== '@all') { signalUser(toUserUuidv4); }
            }
        }
    }

    // Manual "mark all as read" button: marks EVERY unread message in this
    // conversation (incl. older unloaded ones) via the dedicated backend endpoint.
    markConversationAsRead = async () => {
        const { toUserUuidv4 } = this.state;
        if (!toUserUuidv4) {
            return;
        }
        const { messageStore } = this.props.stores;
        const snapshot = this.captureScroll();
        await messageStore.markAllConversationRead(toUserUuidv4, this.loadedLimit);
        this.restoreScroll(snapshot, { stickToBottom: true });
        if (toUserUuidv4 && toUserUuidv4 !== '@all') { signalUser(toUserUuidv4); }
    }

    setFocus = e => {
        this.searchInputField.focus();
    }

    setFocusMessage = e => {
        this.messageInputField.focus();
    }

    handleKeyDown = e => {
        const { messageStore } = this.props.stores;
        const { users, search } = messageStore;

        this.setFocus();

        const { selectedIndex } = this.state;
        if (e.keyCode === 38) {
            // Arrow up key pressed
            e.preventDefault();
            e.stopPropagation();
            // console.log('Arrow up key pressed');
            let idx = selectedIndex - 1;
            if (idx < 0) {
                idx = users.length - 1;
            }
            this.setState({
                selectedIndex: idx,
            });
            // if (this.lines[idx]) {
            //     this.lines[idx].scrollIntoView({ behavior: 'smooth', block: 'center', container: this.linesContainer });
            // }
        } else if (e.keyCode === 40) {
            // Arrow down key pressed
            e.preventDefault();
            e.stopPropagation();
            // console.log('Arrow down key pressed');
            let idx = selectedIndex + 1;
            if (idx >= users.length) {
                idx = 0;
            }
            this.setState({
                selectedIndex: idx,
            });
            // if (this.lines[idx]) {
            //     this.lines[idx].scrollIntoView({ behavior: 'smooth', block: 'center', container: this.linesContainer });
            // }
        } else if (e.keyCode === 13) {
            // Enter key pressed
            e.preventDefault();
            e.stopPropagation();
            // console.log('Enter key pressed');
            this.chooseLine(e, selectedIndex || 0);
            this.clearSearch();
        } else if (e.keyCode === 27) {
            // Esc key pressed
            e.preventDefault();
            e.stopPropagation();
            // console.log('Esc key pressed');
            this.clearSearch();
        }
    }

    clearSearch = () => {
        const { messageStore } = this.props.stores;
        messageStore.updateKeyValue('search', '');
        messageStore.updateKeyValue('users', []);
        this.setFocus();
    }

    searchInput = (e) => {
        const { messageStore } = this.props.stores;
        const value = e.target.value;
        messageStore.updateKeyValue('search', value);

        clearTimeout(this.searchTimer);
        if (value.length >= 3) {
            this.searchTimer = setTimeout(async () => {
                this.doSearch();
            }, 300);
        }
    }

    doSearch = async () => {
        this.setState({ searching: true });
        const { messageStore } = this.props.stores;
        const { search } = messageStore;
        await messageStore.searchForUsers(search);
        this.setState({ searching: false, selectedIndex: 0 });
    }

    searchForUsers = async (e) => {
        const { messageStore } = this.props.stores;
        const value = e.target.value;
        if (value && value.length > 2) {
            await messageStore.searchForUsers(value);
        } else {
            messageStore.updateKeyValue('users', []);
        }
    }

    chooseLine = async (e, idx) => {
        const { messageStore } = this.props.stores;
        const { users } = messageStore;
        if (users[idx]) {
            this.setState({ toUserUuidv4: users[idx].uuidv4 });
            await this.loadConversation(users[idx].uuidv4, this.limitMessages, 0);
        }
    }

    messageInput = (e) => {
        const { messageStore, userStore } = this.props.stores;
        const value = e.target.value;
        messageStore.updateKeyValue('messageContent', value);
        // Grow the textarea on input. iOS Safari does not fire reliable keyup for
        // the soft keyboard, so the onKeyUp-based resize never runs while typing.
        localUtil.resizeTextarea(e);
        // Persist a per-conversation draft, but not while editing an existing
        // message (that text is the edit body, not a draft).
        if (this.state.toUserUuidv4 && !messageStore.messageUuidv4) {
            setDraft(this.state.toUserUuidv4, value);
        }
        const { user } = userStore;
        const to = this.state.toUserUuidv4;
        if (to && to !== '@all' && to !== (user && user.uuidv4)) {
            const now = Date.now();
            if (shouldSendTyping(this.lastTypingSentMs, now)) {
                this.lastTypingSentMs = now;
                sendTyping(conversationId(user.uuidv4, to), { uuidv4: user.uuidv4, name: localUtil.displayName(user) });
            }
        }
    }

    handleMessageKeyDown = async (e) => {
        const { messageStore } = this.props.stores;
        const { messageContent, currentUser } = messageStore;

        this.setFocusMessage();

        if (e.isComposing || e.key === 'Process') return;

        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            messageStore.updateKeyValue('messageContent', '');
            return;
        }

        if (e.key === 'Enter') {
            if (e.shiftKey) {
                return; // allow newline
            }
            e.preventDefault();
            e.stopPropagation();

            // optional: skip empty messages
            if (!messageContent || !messageContent.trim()) return;

            this.sendMessage();
        }
    }

    sendMessage = async () => {
        const { toUserUuidv4 } = this.state;
        const dialogAction = toUserUuidv4 === '@all' ? beginDialogAction(this, 'sendMessage', () => [
            this.state.toUserUuidv4,
            this.props.stores.messageStore.messageContent,
        ]) : null;
        if (toUserUuidv4 === '@all' && !dialogAction) return false;
        const broadcastImages = [...this.state.attachedImages];
        try {
            if (!toUserUuidv4) {
                return;
            }
            const { messageStore, userStore } = this.props.stores;
            const { user = {} } = userStore;
            const { messageContent, messageUuidv4 } = messageStore;
            const body = (messageContent || '').trim();
            if (!body && this.state.attachedImages.length === 0) {
                return;
            }

            const isAll = toUserUuidv4 === '@all';
            // Edit only applies to direct conversations — broadcasts always send anew
            // (matches the prior behaviour where the @all path ignored messageUuidv4).
            const editUuidv4 = !isAll && messageUuidv4 ? messageUuidv4 : null;
            const nowIso = new Date().toISOString();

            if (isAll && !((await appConfirm('Are you sure you want to send this message to ALL users in your company?')) && dialogAction.isCurrent())) {
                return;
            }

            // Clear the input immediately so sending feels instant.
            messageStore.updateKeyValue('messageContent', '');
            messageStore.updateKeyValue('messageUuidv4', null);

            if (editUuidv4) {
                // Optimistic edit: update the existing bubble in place, then persist.
                messageStore.patchMessageByUuid(editUuidv4, buildEditPatch({ body, editUuidv4, toUserUuidv4 }));
                await messageStore.sendMessage(toUserUuidv4, body, { editUuidv4, limit: this.loadedLimit });
                this.setState({ attachedImages: [], replyToUuidv4: null });
                return;
            }

            const tempUuid = uuidv4();
            if (isAll) {
                const images = broadcastImages;
                messageStore.addOptimisticMessage(buildOptimisticMessage({
                    kind: 'broadcast', body, fromUser: user.uuidv4, fromName: localUtil.displayName(user), toUserUuidv4, tempUuid, nowIso, images,
                }));
                await messageStore.sendMessageToAll(body, { tempUuid, images, limit: this.loadedLimit });
                this.setState({ attachedImages: [] });
                clearDraft('@all');
                signalCustomer(userStore.customer && userStore.customer.id);
                this.lastTypingSentMs = 0;
            } else {
                const images = this.state.attachedImages;
                const replyToUuidv4 = this.state.replyToUuidv4;
                messageStore.addOptimisticMessage(buildOptimisticMessage({
                    kind: 'new', body, fromUser: user.uuidv4, toUserUuidv4, tempUuid, nowIso, images, replyToUuidv4,
                }));
                this.setState({ attachedImages: [], replyToUuidv4: null });
                clearDraft(toUserUuidv4);
                await messageStore.sendMessage(toUserUuidv4, body, { tempUuid, images, replyToUuidv4, limit: this.loadedLimit });
                signalUser(toUserUuidv4);
                this.lastTypingSentMs = 0;
            }
        } finally {
            dialogAction?.finish();
        }
    }

    retryMessage = async (message) => {
        if (!message || !message.uuidv4 || !message._retry) {
            return;
        }
        const { messageStore } = this.props.stores;
        const messageid = message.uuidv4;
        const retry = message._retry;
        messageStore.patchMessageByUuid(messageid, { pending: true, failed: false });
        if (retry.kind === 'edit') {
            await messageStore.sendMessage(retry.toUserUuidv4, retry.body, { editUuidv4: retry.editUuidv4, limit: this.loadedLimit });
        } else if (retry.kind === 'broadcast') {
            await messageStore.sendMessageToAll(retry.body, { tempUuid: messageid, limit: this.loadedLimit });
        } else {
            await messageStore.sendMessage(retry.toUserUuidv4, retry.body, { tempUuid: messageid, limit: this.loadedLimit });
        }
    }

    removeMessage = async (message) => {
        const dialogAction = beginDialogAction(this, 'removeMessage', () => [
            this.state.toUserUuidv4,
            message?.uuidv4,
        ]);
        if (!dialogAction) return false;
        try {
            if (!message || !message.uuidv4) {
                return;
            }
            if (!((await appConfirm('Are you sure you want to delete this message?')) && dialogAction.isCurrent())) {
                return;
            }
            const { toUserUuidv4 } = this.state;
            const { messageStore } = this.props.stores;
            await messageStore.deleteMessage(toUserUuidv4, message.uuidv4);
            if (toUserUuidv4 && toUserUuidv4 !== '@all') { signalUser(toUserUuidv4); }
            await this.loadAll();
        } finally {
            dialogAction.finish();
        }
    }

    editMessage = (message) => {
        if (!message || !message.uuidv4) {
            return;
        }
        const { messageStore } = this.props.stores;
        messageStore.updateKeyValue('messageUuidv4', message.uuidv4);
        messageStore.updateKeyValue('messageContent', message.body || '');
    }

    onBlur = (e) => {
        localUtil.resizeTextarea(e);
    }

    toggleReadBy = (messageUuidv4) => {
        this.setState(prevState => ({
            expandedReadBy: {
                ...prevState.expandedReadBy,
                [messageUuidv4]: !prevState.expandedReadBy[messageUuidv4],
            },
        }));
    }

    // Messages may contain internal links (e.g. "Open signing requests" -> #/signing-requests).
    // Those navigate to a full route that the inbox drawer overlays, so close all drawers on
    // click to reveal the destination page. External (http) links are left untouched.
    handleMessageBodyClick = (e) => {
        const link = e.target.closest('a');
        if (!link) {
            return;
        }
        const href = link.getAttribute('href') || '';
        const isInternal = href.startsWith('#/') || (href.startsWith('/') && !href.startsWith('//'));
        if (isInternal) {
            const { appState } = this.props.stores;
            appState.toggleDrawer(null, 1);
            appState.toggleDrawerRight(null, 1);
        }
    }

    replyToUser = async (message) => {
        const useruuidv4 = message && message.fromUser;
        if (!useruuidv4) {
            return;
        }
        this.setState({ toUserUuidv4: useruuidv4 });
        await this.loadConversation(useruuidv4, this.limitMessages, 0);
        requestAnimationFrame(() => this.scrollMessagesToBottom());
    }

    startReply = (message) => {
        if (message && message.uuidv4) {
            this.setState({ replyToUuidv4: message.uuidv4 });
            this.setFocusMessage();
        }
    };

    toggleExpand = (uuid) => {
        this.setState(prev => ({ openMessageUuid: prev.openMessageUuid === uuid ? null : uuid }));
    };

    toggleQuickLike = async (message) => {
        if (!message || !message.id) {
            return;
        }
        const { likeStore, userStore, messageStore } = this.props.stores;
        const { user } = userStore;
        const type = 'message';
        const objectId = message.id;
        const hasLiked = (message.likes || []).some(l => l.user === user.id);
        if (hasLiked) {
            await likeStore.delete(1, 'id', { type, objectId });
        } else {
            await likeStore.insert({ type, objectId, likeType: 'like' });
        }
        const likes = await likeStore.load({ query: { type, objectId }, skipUpdate: true });
        messageStore.patchMessageByUuid(message.uuidv4, { likes });
    };

    reactToMessage = async (message, likeType) => {
        if (!message || !message.id || !likeType) {
            return;
        }
        const { likeStore, userStore, messageStore } = this.props.stores;
        const { user } = userStore;
        const type = 'message';
        const objectId = message.id;
        const current = (message.likes || []).find(l => l.user === user.id);
        if (current && (current.likeType || 'like') === likeType) {
            await likeStore.delete(1, 'id', { type, objectId });
        } else if (current) {
            await likeStore.changeReaction({ type, objectId, likeType });
        } else {
            await likeStore.insert({ type, objectId, likeType });
        }
        const likes = await likeStore.load({ query: { type, objectId }, skipUpdate: true });
        messageStore.patchMessageByUuid(message.uuidv4, { likes });
    };

    cancelReply = () => this.setState({ replyToUuidv4: null });

    gotoConversation = async (e) => {
        e.stopPropagation();
        e.preventDefault();
        const { uuidv4 } = e.target.closest('.userLine').dataset;
        this.setState({ toUserUuidv4: uuidv4 });
        await this.loadConversation(uuidv4, this.limitMessages, 0);
        requestAnimationFrame(() => this.scrollMessagesToBottom());
    }

    gotoConversationList = (e) => {
        e.stopPropagation();
        e.preventDefault();
        this.setState({ toUserUuidv4: null }, () => {
            this.loadConversationList();
        });
    }

    messageSearchInput = (e) => {
        const value = e.target.value;
        this.setState({ messageSearch: value });
        const { messageStore } = this.props.stores;
        clearTimeout(this.messageSearchTimer);
        if (!value || value.trim().length < 2) {
            messageStore.updateKeyValue('messageSearchResults', []);
            return;
        }
        this.messageSearchTimer = setTimeout(() => messageStore.searchMessages(value), 300);
    };

    openSearchResult = async (e) => {
        const { uuidv4: targetUuidv4 } = e.currentTarget.dataset;
        if (!targetUuidv4) { return; }
        this.setState({ messageSearch: '' });
        this.props.stores.messageStore.updateKeyValue('messageSearchResults', []);
        this.setState({ toUserUuidv4: targetUuidv4 });
        await this.loadConversation(targetUuidv4, this.limitMessages, 0);
    };

    componentDidMount() {
        this.loadAll();
        requestAnimationFrame(() => this.scrollMessagesToBottom());
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.uuidv4 !== this.props.uuidv4) {
            this.loadAll(nextProps);
        }
    }

    componentWillUnmount() {
        if (this.markAsReadTimer) {
            clearTimeout(this.markAsReadTimer);
            this.markAsReadTimer = null;
        }
        if (this.searchTimer) {
            clearTimeout(this.searchTimer);
            this.searchTimer = null;
        }
        if (this.conversationListInterval) {
            clearInterval(this.conversationListInterval);
            this.conversationListInterval = null;
        }
        if (this.conversationInterval) {
            clearInterval(this.conversationInterval);
            this.conversationInterval = null;
        }
        if (this.scrollTimer) {
            clearTimeout(this.scrollTimer);
            this.scrollTimer = null;
        }
        if (this.signalUnsub) { this.signalUnsub(); this.signalUnsub = null; }
        if (this.customerSignalUnsub) { this.customerSignalUnsub(); this.customerSignalUnsub = null; }
        if (this.typingUnsub) { this.typingUnsub(); this.typingUnsub = null; }
        if (this.typingClearTimer) { clearTimeout(this.typingClearTimer); this.typingClearTimer = null; }
        if (this.messageSearchTimer) { clearTimeout(this.messageSearchTimer); this.messageSearchTimer = null; }
    }

    openFullscreen = (images = []) => {
        this.setState({ showFullscreen: true, fullscreenImages: images });
    }

    closeFullscreen = () => {
        this.setState({ showFullscreen: false, fullscreenImages: [] });
    }

    uploadImageFile = (file) => new Promise((resolve, reject) => {
        const formData = new FormData();
        formData.append('files[]', file, encodeURIComponent(file.name));
        const xhr = new XMLHttpRequest();
        xhr.addEventListener('readystatechange', () => {
            if (xhr.readyState === 4) {
                if (xhr.status === 201 || xhr.status === 200) {
                    try {
                        const response = JSON.parse(xhr.responseText);
                        const uploaded = response?.data?.files?.[0];
                        if (uploaded) { resolve(uploaded); } else { reject(new Error('No file returned')); }
                    } catch (err) { reject(err); }
                } else { reject(new Error(`Upload failed: ${xhr.status}`)); }
            }
        });
        xhr.open('POST', `${util.getApiServer()}/api/fileupload`);
        xhr.setRequestHeader('Authorization', `Bearer ${util.getJwtToken()}`);
        xhr.send(formData);
    });

    addUploadedImage = (uploaded, idx = 0) => {
        const withKey = { ...uploaded, md5: uploaded.md5 || `${uploaded.s3Link || uploaded.name || ''}-${idx}` };
        this.setState(s => ({ attachedImages: [...s.attachedImages, withKey] }));
    };

    openImagePicker = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.multiple = true;
        input.addEventListener('change', async (ev) => {
            const files = Array.from(ev.target.files || []);
            this.setState({ uploadingImages: true });
            for (let i = 0; i < files.length; i += 1) {
                try { const up = await this.uploadImageFile(files[i]); this.addUploadedImage(up, i); } catch (err) { /* surfaced below */ }
            }
            this.setState({ uploadingImages: false });
        });
        document.body.appendChild(input);
        input.click();
    };

    uploadFromCamera = async (file) => {
        this.setState({ uploadingImages: true });
        try { const up = await this.uploadImageFile(file); this.addUploadedImage(up); } catch (err) { /* noop */ }
        this.setState({ uploadingImages: false, showCamera: false });
    };

    removeAttachedImage = (e) => {
        const { md5 } = e.currentTarget.dataset;
        this.setState(s => ({ attachedImages: s.attachedImages.filter(img => img.md5 !== md5) }));
    };

    toggleCamera = () => this.setState(s => ({ showCamera: !s.showCamera }));

    toggleEmojiPicker = (e) => {
        e.stopPropagation();
        e.preventDefault();
        this.setState(s => ({ showEmojiPicker: !s.showEmojiPicker }));
    };

    addEmoji = (emoji) => {
        const input = this.messageInputField;
        if (!input) { return; }
        const start = input.selectionStart;
        const end = input.selectionEnd;
        const value = input.value || '';
        input.value = value.slice(0, start) + emoji + value.slice(end);
        input.dispatchEvent(new Event('input', { bubbles: true }));
    };

    renderBroadcastStats = (message, isOwnMessage) => {
        const { userStore } = this.props.stores;
        const { isAdmin } = userStore;
        if (!isAdmin || !message.broadcastRecipients || message.broadcastRecipients.length === 0) {
            return null;
        }
        const { expandedReadBy } = this.state;
        const isExpanded = expandedReadBy[`${message.uuidv4}-details`];
        const isExpandedRecipients = expandedReadBy[`${message.uuidv4}-recipients`];
        const isExpandedRead = expandedReadBy[`${message.uuidv4}-read`];
        const maxVisible = 3;

        const recipients = message.broadcastRecipients || [];
        const readByUsers = message.readByUsers || [];
        const readUuidv4s = readByUsers.map(u => u.uuidv4);
        const unreadUsers = recipients.filter(u => !readUuidv4s.includes(u.uuidv4));

        const visibleRecipients = isExpandedRecipients ? recipients : recipients.slice(0, maxVisible);
        const hasMoreRecipients = recipients.length > maxVisible;
        const hiddenRecipientsCount = recipients.length - maxVisible;

        const visibleRead = isExpandedRead ? readByUsers : readByUsers.slice(0, maxVisible);
        const hasMoreRead = readByUsers.length > maxVisible;
        const hiddenReadCount = readByUsers.length - maxVisible;

        return (
            <div class={`d-flex flex-column w-100 align-items-${isOwnMessage ? 'end' : 'start'} mb-2`}>
                <small
                    class='text-muted'
                    style='font-size: 0.7em; cursor: pointer;'
                    onClick={() => this.toggleReadBy(`${message.uuidv4}-details`)}
                >
                    <i class='fa-solid fa-paper-plane me-1' />
                    <Text id='inbox.sentTo'>Sent to</Text> {recipients.length}
                    <span class='mx-1'>•</span>
                    <i class='fa-solid fa-eye me-1' />
                    <Text id='inbox.readByCount'>Read by</Text> {readByUsers.length}
                    {unreadUsers.length > 0 && (
                        <span class='text-warning ms-1'>
                            ({unreadUsers.length} <Text id='inbox.unread'>unread</Text>)
                        </span>
                    )}
                    <i class={`fa-solid fa-chevron-${isExpanded ? 'up' : 'down'} ms-2`} style='font-size: 0.8em;' />
                </small>

                {isExpanded && (<>
                    <small class='text-muted mt-1' style='font-size: 0.65em;'>
                        <i class='fa-solid fa-users me-1' />
                        <Text id='inbox.recipients'>Recipients</Text>: {visibleRecipients.map(u => u.name).join(', ')}
                        {hasMoreRecipients && !isExpandedRecipients && (
                            <span class='text-primary ms-1' style='cursor: pointer;' onClick={(e) => { e.stopPropagation(); this.toggleReadBy(`${message.uuidv4}-recipients`); }}>
                                +{hiddenRecipientsCount} <Text id='inbox.more'>more</Text>
                            </span>
                        )}
                        {hasMoreRecipients && isExpandedRecipients && (
                            <span class='text-primary ms-1' style='cursor: pointer;' onClick={(e) => { e.stopPropagation(); this.toggleReadBy(`${message.uuidv4}-recipients`); }}>
                                <Text id='inbox.showLess'>show less</Text>
                            </span>
                        )}
                    </small>

                    {readByUsers.length > 0 && (
                        <small class='text-muted mt-1' style='font-size: 0.65em;'>
                            <i class='fa-solid fa-check-double me-1 text-success' />
                            <Text id='inbox.readBy'>Read by</Text>: {visibleRead.map(u => u.name).join(', ')}
                            {hasMoreRead && !isExpandedRead && (
                                <span class='text-primary ms-1' style='cursor: pointer;' onClick={(e) => { e.stopPropagation(); this.toggleReadBy(`${message.uuidv4}-read`); }}>
                                    +{hiddenReadCount} <Text id='inbox.more'>more</Text>
                                </span>
                            )}
                            {hasMoreRead && isExpandedRead && (
                                <span class='text-primary ms-1' style='cursor: pointer;' onClick={(e) => { e.stopPropagation(); this.toggleReadBy(`${message.uuidv4}-read`); }}>
                                    <Text id='inbox.showLess'>show less</Text>
                                </span>
                            )}
                        </small>
                    )}
                </>)}
            </div>
        );
    }

    renderDayDivider = (labelKey) => {
        let label;
        if (labelKey === 'today') {
            label = <Text id='inbox.today'>Today</Text>;
        } else if (labelKey === 'yesterday') {
            label = <Text id='inbox.yesterday'>Yesterday</Text>;
        } else if (labelKey === 'unknown') {
            label = '';
        } else {
            label = labelKey;
        }
        return (
            <div class='d-flex justify-content-center my-2'>
                <span class='badge rounded-pill bg-body-secondary text-body-secondary'>{label}</span>
            </div>
        );
    }

    render() {
        const {
            disabled,
            searching,
            selectedIndex,
            focus,
            loadingMore,
        } = this.state;
        const {
            titleText,
            sendMessageText,
            searchMessagesText,
        } = this.props;

        const { userStore, messageStore } = this.props.stores;
        const { user, language } = userStore;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');
        const { currentUser, users, conversationsUsers, messages, search, messageContent, messageUuidv4, messageSearchResults } = messageStore;

        // In a conversation, lay the drawer out as a height-filling flex column so the
        // message list flexes/scrolls and the compose bar stays pinned at the bottom
        // (fixes the iOS "compose below the fold" issue). The conversation LIST view
        // keeps the original centered, naturally-scrolling layout.
        const inConversation = !!(currentUser && currentUser.uuidv4);

        return (<>
            <div
                class={`w-100 d-flex position-relative ${inConversation ? 'flex-column h-100' : 'justify-content-center mt-5'}`}
                style={inConversation ? 'padding-top: 3.25rem;' : ''}
            >
                <div
                    class={`w-100 ${inConversation ? 'd-flex flex-column flex-grow-1' : 'h-100'}`}
                    style={`padding-top: 0px; padding-bottom: 0px;${inConversation ? ' min-height: 0;' : ''}`}
                >

                    <div class={`w-100 d-flex flex-column ${inConversation ? 'flex-grow-1' : ''}`} style={inConversation ? 'min-height: 0;' : ''}>
                        {(currentUser && currentUser.uuidv4) ? <>
                            <div class={`d-flex flex-column mx-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} px-4 py-1`}>
                                <div class={`d-flex flex-row align-items-center p-1 rounded-lg`}>
                                    <div
                                        class='d-flex flex-row align-items-center'
                                        role='button'
                                        style='cursor: pointer;'
                                        onClick={this.gotoConversationList}
                                    >
                                        <span class='btn btn-primary rounded-pill'>
                                            <i class='fa-solid fa-list-ul'></i>
                                        </span>
                                        <h5 class='ms-3 mb-0'>
                                            <Text id='inbox.allConversations'>All conversations</Text>
                                        </h5>
                                    </div>
                                    <button
                                        class='btn btn-sm btn-outline-secondary rounded-pill ms-auto'
                                        onClick={this.markConversationAsRead}
                                    >
                                        <i class='fa-solid fa-check-double me-1' />
                                        <Text id='inbox.markAllAsRead'>Mark all as read</Text>
                                    </button>
                                </div>
                            </div>
                        </> : <>
                            <div class={`d-flex flex-column mx-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} px-4 py-3`}>
                                <div class={`d-flex flex-row align-items-center p-2 rounded-lg`}>
                                    <div>
                                        <h5 class='ms-3 mb-0'>
                                            <i class='fa-solid fa-inbox me-2' />
                                            <Text id='inbox.title'>Inbox</Text>
                                        </h5>
                                    </div>
                                </div>
                            </div>
                            <div class={`d-flex flex-column mx-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} px-4 py-3 mt-3`}>
                                <div class='d-flex flex-row align-items-center mb-3'>

                                    <div class='input-group position-relative mb-0'>
                                        <div class='input-group-text rounded-pill-left'>
                                            <i class='fa-regular fa-magnifying-glass'></i>
                                        </div>

                                        <input
                                            disabled={disabled}
                                            class={`form-control form-control-sm ${!search ? 'rounded-pill-right' : ''}`}
                                            type='text'
                                            value={search}
                                            placeholder={titleText}
                                            onInput={this.searchInput}
                                            onKeyDown={this.handleKeyDown}
                                            onFocus={this.setFocus}
                                            ref={c => this.searchInputField = c}
                                            // onBlur={this.unsetFocus}
                                        />
                                        {search && <>
                                            <button class='btn btn-sm btn-danger rounded-pill-right' type='button' onClick={this.clearSearch}>
                                                <i class='fa-solid fa-circle-xmark'></i>
                                            </button>
                                        </>}
                                        <div class='position-absolute' style='right: 55px; top: 5px; font-size: 1.2em; z-index: 1000;'>
                                            {searching && <i class='fa-solid fa-spinner fa-spin text-secondary' />}
                                        </div>
                                    </div>

                                </div>

                                <div class='d-flex flex-column'>
                                    {users.map((u, idx) => (
                                        <div
                                            class={`d-flex flex-row align-items-center mb-3 p-2 rounded-lg ${selectedIndex === idx ? 'bg-primary' : ''} userLine`}
                                            onClick={this.gotoConversation}
                                            data-uuidv4={u.uuidv4}
                                            style='cursor: pointer;'
                                        >
                                            {u.isAllUsers ? <>
                                                <div class='rounded-circle bg-warning text-dark d-flex align-items-center justify-content-center mr-3 flex-shrink-0' style='width:40px; height:40px; font-size:1.2em;'>
                                                    <i class='fa-solid fa-bullhorn' />
                                                </div>
                                            </> : localUtil.hasImage({ user: u }) ? <>
                                                <img src={localUtil.getImage({ user: u })} alt='' class='rounded-circle bg-light mr-3 flex-shrink-0' style='width:40px; height:40px; object-fit:cover;' />
                                            </> : <>
                                                <div class='rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center mr-3 flex-shrink-0' style='width:40px; height:40px; font-size:1.2em;'>
                                                    {localUtil.displayNameShort(u)}
                                                </div>
                                            </>}
                                            <div class='d-flex flex-column ms-3'>
                                                <div class='font-weight-bold'>{u.isAllUsers ? <Text id='inbox.sendToAllUsers'>Send to all users</Text> : localUtil.displayName(u, true)}</div>
                                                {/* <div class='text-truncate' style='max-width: 200px;'>{u.email}</div> */}
                                            </div>
                                        </div>
                                    ))}
                                    {search && users.length === 0 && (
                                        <div class='text-muted'>
                                            <Text id='inbox.noUsers'>No users found...</Text>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>}

                        {(currentUser && currentUser.uuidv4) ? <>
                            <div class={`d-flex flex-column mx-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} px-4 py-0 mt-3`}>
                                <div class={`d-flex flex-row align-items-center p-2 rounded-lg`}>
                                    {currentUser.isAllUsers ? <>
                                        <div class='rounded-circle bg-warning text-dark d-flex align-items-center justify-content-center mr-3 flex-shrink-0' style='width:40px; height:40px; font-size:1.2em;'>
                                            <i class='fa-solid fa-bullhorn' />
                                        </div>
                                    </> : localUtil.hasImage({ user: currentUser }) ? <>
                                        <div class='position-relative flex-shrink-0 mr-3'>
                                            <img src={localUtil.getImage({ user: currentUser })} alt='' class='rounded-circle bg-light' style='width:40px; height:40px; object-fit:cover;' />
                                            <PresenceDot online={currentUser.online} size={12} />
                                        </div>
                                    </> : <>
                                        <div class='position-relative flex-shrink-0 mr-3'>
                                            <div class='rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center' style='width:40px; height:40px; font-size:1.2em;'>
                                                {localUtil.displayNameShort(currentUser)}
                                            </div>
                                            <PresenceDot online={currentUser.online} size={12} />
                                        </div>
                                    </>}
                                    <div class='d-flex flex-column ms-3'>
                                        <div class='font-weight-bold'>{currentUser.isAllUsers ? <Text id='inbox.broadcastToAllUsers'>Broadcast to all users</Text> : localUtil.displayName(currentUser, true)}</div>
                                        {/* <div class='text-truncate' style='max-width: 200px;'>{currentUser.email}</div> */}
                                    </div>
                                </div>
                            </div>
                            <div
                                class={`d-flex flex-column mx-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} px-4 py-3 mt-1 position-relative overflow-auto`}
                                style={`
                                    flex: 1 1 auto;
                                    min-height: 0;
                                    overflow-anchor: none;
                                `}
                                ref={c => this.messageContainer = c}
                                onScroll={this.handleMessagesScroll}
                            >

                                {/* Messages: {messages?.length}<br /> */}
                                {/* messageUuidv4: {messageUuidv4}<br /> */}
                                {/* Overlaid (position-absolute) so it never changes scrollHeight,
                                    keeping the load-more scroll-position math exact. */}
                                {loadingMore && <>
                                    <div
                                        class='position-absolute start-50 translate-middle-x d-flex align-items-center justify-content-center p-2'
                                        style='top: 8px; z-index: 1000;'
                                    >
                                        <i class='fa-solid fa-spinner fa-spin text-secondary' />
                                    </div>
                                </>}

                                {buildMessageRenderList(messages, user.uuidv4).map(item => {
                                    if (item.type === 'day') {
                                        return this.renderDayDivider(item.labelKey);
                                    }
                                    if (item.type === 'newMessages') {
                                        return (
                                            <div
                                                ref={c => { this.firstUnreadEl = c; }}
                                                class='d-flex justify-content-center my-2'
                                            >
                                                <span class='badge rounded-pill bg-primary'>
                                                    <Text id='inbox.newMessages'>New messages</Text>
                                                </span>
                                            </div>
                                        );
                                    }
                                    const message = item.message;
                                    const isOwnMessage = message.fromUser === user.uuidv4;
                                    const isBroadcast = currentUser.isAllUsers || message.isBroadcast === 1;
                                    return (<>
                                        <InboxMessage
                                            key={message.uuidv4}
                                            stores={this.props.stores}
                                            message={message}
                                            messages={messages}
                                            isOwnMessage={isOwnMessage}
                                            isBroadcast={isBroadcast}
                                            language={language}
                                            expanded={this.state.openMessageUuid === message.uuidv4}
                                            onToggleExpand={this.toggleExpand}
                                            onQuickLike={this.toggleQuickLike}
                                            onReact={this.reactToMessage}
                                            onReply={(!isOwnMessage && !isBroadcast) ? this.startReply : undefined}
                                            onReplyToUser={(!isOwnMessage && isBroadcast) ? this.replyToUser : undefined}
                                            onEdit={isOwnMessage ? this.editMessage : undefined}
                                            onDelete={isOwnMessage ? this.removeMessage : undefined}
                                            onRetry={this.retryMessage}
                                            onImageClick={this.openFullscreen}
                                            onBodyLinkClick={this.handleMessageBodyClick}
                                        />
                                        {this.renderBroadcastStats(message, isOwnMessage)}
                                    </>);
                                })}

                            </div>
                            {this.state.typingName ? (
                                <div class='text-body-secondary small px-4 pb-1'>
                                    <i class='fa-solid fa-ellipsis fa-fade me-1' />
                                    {this.state.typingName} <Text id='inbox.isTyping'>is typing…</Text>
                                </div>
                            ) : null}
                            <div class={`d-flex flex-column mx-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} px-4 py-3 mt-1`}>
                                {this.state.uploadingImages ? (
                                    <div class='text-body-secondary small mb-1'>
                                        <i class='fa-solid fa-spinner fa-spin me-1' /><Text id='inbox.uploadingImage'>Uploading…</Text>
                                    </div>
                                ) : null}
                                {this.state.attachedImages.length > 0 ? (
                                    <div class='d-flex flex-row flex-wrap gap-2 mb-2'>
                                        {this.state.attachedImages.map(img => (
                                            <div key={img.md5} class='position-relative' style='width:64px;height:64px;'>
                                                <img src={img.lowResBase64 ? `data:image/jpeg;base64,${img.lowResBase64}` : (img.s3SmallLink || img.s3MediumLink)} class='w-100 h-100 rounded' style='object-fit:cover;' alt='' />
                                                <button type='button' class='btn btn-sm btn-danger position-absolute top-0 end-0 p-0 px-1' data-md5={img.md5} onClick={this.removeAttachedImage}>
                                                    <i class='fa-solid fa-xmark' />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                ) : null}
                                {this.state.replyToUuidv4 ? (() => {
                                    const quoted = findQuotedMessage(messages, this.state.replyToUuidv4);
                                    return (
                                        <div class='d-flex align-items-center justify-content-between bg-body-secondary rounded px-2 py-1 mb-1'>
                                            <small class='text-body-secondary'>
                                                <i class='fa-solid fa-reply me-1' />
                                                <Text id='inbox.replyingTo'>Replying to</Text>: {quoteSnippet(quoted ? quoted.body : '', 60)}
                                            </small>
                                            <button type='button' class='btn btn-sm btn-link text-body-secondary p-0' onClick={this.cancelReply}>
                                                <i class='fa-solid fa-xmark' />
                                            </button>
                                        </div>
                                    );
                                })() : null}
                                <div class='d-flex flex-column mb-3'>
                                    <Localizer>
                                        <textarea
                                            class='form-control w-100'
                                            value={messageContent}
                                            placeholder={sendMessageText}
                                            onInput={this.messageInput}
                                            onKeyDown={this.handleMessageKeyDown}
                                            onFocus={this.setFocusMessage}
                                            ref={c => this.messageInputField = c}
                                            rows={1}
                                            onKeyUp={localUtil.resizeTextarea}
                                            onBlur={this.onBlur}
                                        ></textarea>
                                    </Localizer>
                                    <div class='d-flex flex-row align-items-center mt-2'>
                                        <button type='button' class='btn btn-link text-muted' onClick={this.toggleCamera}>
                                            <i class='fa-duotone fa-camera' />
                                        </button>
                                        <button type='button' class='btn btn-link text-muted' onClick={this.openImagePicker}>
                                            <i class='fa-duotone fa-image' />
                                        </button>
                                        <button type='button' class='btn btn-link text-muted' onClick={this.toggleEmojiPicker}>
                                            <i class={`${this.state.showEmojiPicker ? 'fa-solid' : 'fa-regular'} fa-face-smile`} />
                                        </button>
                                        <button
                                            class='btn btn-primary ms-auto'
                                            onClick={this.sendMessage}
                                        >
                                            <i class="fa-duotone fa-paper-plane"></i>
                                        </button>
                                    </div>
                                </div>
                                {this.state.showEmojiPicker ? (
                                    <EmojiPicker stores={this.props.stores} addEmoji={this.addEmoji} />
                                ) : null}
                            </div>
                        </> : <>
                            <div class={`d-flex flex-column mx-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} px-4 py-2 mt-3`}>
                                <div class='input-group mb-2'>
                                    <span class='input-group-text'><i class='fa-regular fa-magnifying-glass' /></span>
                                    <input
                                        class='form-control'
                                        type='text'
                                        value={this.state.messageSearch}
                                        placeholder={searchMessagesText}
                                        onInput={this.messageSearchInput}
                                    />
                                </div>
                                {messageSearchResults.length > 0 ? (
                                    <div class='d-flex flex-column'>
                                        {messageSearchResults.map(m => (
                                            <button
                                                key={m.uuidv4}
                                                type='button'
                                                class='list-group-item list-group-item-action text-start'
                                                data-uuidv4={m.counterpartUuid}
                                                onClick={this.openSearchResult}
                                            >
                                                <div class='fw-bold'>{m.counterpartName || ' '}</div>
                                                <small class='text-body-secondary'>
                                                    {m.body && m.body.length > 100 ? `${m.body.substring(0, 100)}…` : m.body}
                                                </small>
                                            </button>
                                        ))}
                                    </div>
                                ) : null}
                                {this.state.messageSearch && this.state.messageSearch.trim().length >= 2 && messageSearchResults.length === 0 ? (
                                    <div class='text-body-secondary'><Text id='inbox.noResults'>No messages found</Text></div>
                                ) : null}
                                {messageSearchResults.length === 0 ? (
                                    <div>
                                        {conversationsUsers.map((u, idx) => (
                                            <div
                                                key={u.uuidv4 || idx}
                                                class={`d-flex flex-row align-items-center my-1 p-2 rounded-lg userLine`}
                                                onClick={this.gotoConversation}
                                                data-uuidv4={u.uuidv4}
                                                style='cursor: pointer;'
                                            >
                                                {u.isAllUsers || u.uuidv4 === '@all' ? <>
                                                    <div class='rounded-circle bg-warning text-dark d-flex align-items-center justify-content-center mr-3 flex-shrink-0' style='width:40px; height:40px; font-size:1.2em;'>
                                                        <i class='fa-solid fa-bullhorn' />
                                                    </div>
                                                </> : localUtil.hasImage({ user: u }) ? <>
                                                    <div class='position-relative flex-shrink-0 mr-3'>
                                                        <img src={localUtil.getImage({ user: u })} alt='' class='rounded-circle bg-light' style='width:40px; height:40px; object-fit:cover;' />
                                                        <PresenceDot online={u.online} size={12} />
                                                    </div>
                                                </> : <>
                                                    <div class='position-relative flex-shrink-0 mr-3'>
                                                        <div class='rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center' style='width:40px; height:40px; font-size:1.2em;'>
                                                            {localUtil.displayNameShort(u)}
                                                        </div>
                                                        <PresenceDot online={u.online} size={12} />
                                                    </div>
                                                </>}

                                                <div class='d-flex flex-column ms-3'>
                                                    <div class='font-weight-bold'>{(u.isAllUsers || u.uuidv4 === '@all') ? <Text id='inbox.broadcastMessages'>Broadcast messages</Text> : localUtil.displayName(u, true)}</div>
                                                    {/* <div class='text-truncate' style='max-width: 200px;'>{u.email}</div> */}
                                                    <small class='text-muted'>
                                                        {u.lastMessage ? <>
                                                            <span>{localUtil.getTimeAgo(u.lastMessage.sentDate)}</span>
                                                            <small class='text-muted px-2'>
                                                                •
                                                            </small>
                                                            {(u.lastMessage.body.length > 100 ? `${u.lastMessage.body.substring(0, 100)}...` : u.lastMessage.body)}
                                                        </> : <i>No messages yet</i>}
                                                    </small>
                                                </div>
                                                <div class='ms-auto'>
                                                    {u.hasUnread ? <span class='badge bg-danger'>{u.unreadCount}</span> : null}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : null}
                            </div>
                        </>}

                    </div>
                </div>
            </div>
            {this.state.showFullscreen ? (
                <ImageScrollerFullscreen
                    stores={this.props.stores}
                    images={this.state.fullscreenImages}
                    onClickClose={this.closeFullscreen}
                />
            ) : null}
            {this.state.showCamera ? (
                <Camera handleUpload={this.uploadFromCamera} closeCamera={this.toggleCamera} />
            ) : null}
        </>);
    }
}

export default Inbox;

